// App.jsx - Main Application Router
import React, { useState, useEffect } from 'react';
import { BrandProvider, useBrand } from './contexts/BrandContext';
import { ToastProvider } from './contexts/ToastContext';
import { CartProvider, useCart } from './contexts/CartContext';
import { DB } from './utils/db';
import { authService } from './services/authService';
import { getToken, gqlRequest } from './services/api';

// Layout Components
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { BrandSwitcher } from './components/layout/BrandSwitcher';

// Patient Components
import { Landing } from './components/patient/Landing';
import { PilotEnrollment } from './components/patient/PilotEnrollment';
import { Register } from './components/patient/Register';
import { Login } from './components/patient/Login';
import { Dashboard } from './components/patient/Dashboard';
import { PrescriptionUpload } from './components/patient/PrescriptionUpload';
import { PrescriptionList } from './components/patient/PrescriptionList';
import { PrescriptionDetail } from './components/patient/PrescriptionDetail';
import { BundleOrder } from './components/patient/BundleOrder';
import { OrderList } from './components/patient/OrderList';
import { OrderDetail } from './components/patient/OrderDetail';
import { Messages } from './components/patient/Messages';
import { Profile } from './components/patient/Profile';

// B2B Components
import { B2BDashboard } from './components/b2b/Dashboard';
import { Catalog } from './components/b2b/Catalog';
import { VendorConnections } from './components/b2b/VendorConnections';
import { PurchaseOrders } from './components/b2b/PurchaseOrders';
import { SpendAnalysis } from './components/b2b/SpendAnalysis';

// Shared Components
import { Chatbot } from './components/shared/Chatbot';
import { CostCalculator } from './components/shared/CostCalculator';
import { AdminPanel } from './components/shared/AdminPanel';
import { Cart } from './components/shared/Cart';
import { Checkout } from './components/shared/Checkout';

function App() {
  // Routing state
  const [page, setPage] = useState('landing');
  const [detailId, setDetailId] = useState(null);
  
  // User & data state
  const [user, setUser] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // UI state
  const [showAdmin, setShowAdmin] = useState(false);
  const [brandSwitcherVisible, setBrandSwitcherVisible] = useState(true);
  
  // Load session on mount
  useEffect(() => {
    loadSession();
    
    // Keyboard shortcut for admin panel (Shift+A)
    const handleKeyPress = (e) => {
      if (e.shiftKey && e.key === 'A') {
        setShowAdmin(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // Load user data when user changes
  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);
  
  const seedDemoAccount = async () => {
    const existing = await DB.get('email:sarah.mitchell@email.com');
    if (!existing) {
      const demoUser = {
        id: 'demo_user',
        firstName: 'Sarah',
        lastName: 'Mitchell',
        email: 'sarah.mitchell@email.com',
        phone: '(555) 123-4567',
        dob: '1990-03-15',
        password: 'demo123',
        address: {
          street: '123 Main St',
          apt: '',
          city: 'Austin',
          state: 'TX',
          zip: '78701'
        },
        insurance: null,
        createdAt: new Date().toISOString(),
        isB2B: false
      };
      await DB.set('user:demo_user', demoUser);
      await DB.set('email:sarah.mitchell@email.com', 'demo_user');
    }

    if (!localStorage.getItem('d2p_demo_api_registered')) {
      try {
        await gqlRequest(`
          mutation AddUser($user: UserInputType!, $forceChangePassword: Boolean, $createSlug: Boolean) {
            addUser(user: $user, forceChangePassword: $forceChangePassword, createSlug: $createSlug) { id }
          }
        `, {
          user: {
            email: 'sarah.mitchell@email.com',
            password: 'demo123',
            roleCode: 'customer-simple',
            userAttributes: [
              { attributeCode: 'firstname', value: 'Sarah' },
              { attributeCode: 'lastname', value: 'Mitchell' },
              { attributeCode: 'phone', value: '(555) 123-4567' },
              { attributeCode: 'dob', value: '1990-03-15' }
            ]
          },
          forceChangePassword: false,
          createSlug: false
        });
        const demoLogin = await gqlRequest(`
          mutation { login(email: "sarah.mitchell@email.com", password: "demo123") { token } }
        `);
        if (demoLogin?.login?.token) {
          const prevToken = localStorage.getItem('d2p_token');
          localStorage.setItem('d2p_token', demoLogin.login.token);
          await gqlRequest(`
            mutation UpdateUser($user: UserInputType!) {
              updateUser(user: $user) { id }
            }
          `, {
            user: { userAttributes: [{ attributeCode: 'buyer-group', value: JSON.stringify({ id: 40 }) }] }
          }, { requireAuth: true });
          if (prevToken) {
            localStorage.setItem('d2p_token', prevToken);
          } else {
            localStorage.removeItem('d2p_token');
          }
        }
        localStorage.setItem('d2p_demo_api_registered', 'true');
      } catch (e) {
        if (e.message && e.message.includes('already exists')) {
          localStorage.setItem('d2p_demo_api_registered', 'true');
        }
      }
    }
  };
  
  const ensureBuyerGroup = async (profile) => {
    if (profile.buyerGroup) return profile;
    try {
      await gqlRequest(`
        mutation UpdateUser($user: UserInputType!) {
          updateUser(user: $user) { id }
        }
      `, {
        user: { userAttributes: [{ attributeCode: 'buyer-group', value: JSON.stringify({ id: 40 }) }] }
      }, { requireAuth: true });
      const refreshed = await authService.getProfile();
      if (refreshed) return refreshed;
    } catch (e) {
      console.warn('Buyer group auto-assign failed:', e.message);
    }
    return profile;
  };

  const loadSession = async () => {
    await seedDemoAccount();
    if (getToken()) {
      try {
        let profile = await authService.getProfile();
        if (profile) {
          profile = await ensureBuyerGroup(profile);
          setUser(profile);
          setPage('dashboard');
          return;
        }
      } catch (e) {
        console.warn('Token session restore failed, trying localStorage fallback:', e.message);
        authService.logout();
      }
    }
    const sessionId = await DB.get('d2p_session');
    if (sessionId) {
      const userData = await DB.get(`user:${sessionId}`);
      if (userData) {
        setUser(userData);
        setPage('dashboard');
      }
    }
  };
  
  const loadUserData = async () => {
    if (!user) return;
    
    const allRxs = await DB.get('d2p_prescriptions') || [];
    const allOrders = await DB.get('d2p_orders') || [];
    
    setPrescriptions(allRxs);
    setOrders(allOrders);
  };
  
  const navigate = (newPage, id = null) => {
    setPage(newPage);
    setDetailId(id);
    window.scrollTo(0, 0);
  };
  
  const handleLogin = (userData) => {
    setUser(userData);
    navigate('dashboard');
  };
  
  const handleRegister = (userData) => {
    setUser(userData);
    navigate('dashboard');
  };
  
  const handleLogout = async () => {
    authService.logout();
    await DB.del('d2p_session');
    setUser(null);
    setPrescriptions([]);
    setOrders([]);
    navigate('landing');
  };
  
  const handleRefresh = () => {
    loadUserData();
  };
  
  // Determine if current page needs authentication
  const authRequired = ![
    'landing',
    'pilot',
    'register',
    'login'
  ].includes(page);
  
  // Redirect to login if auth required but no user
  useEffect(() => {
    if (authRequired && !user) {
      navigate('login');
    }
  }, [authRequired, user]);
  
  // Public pages (no layout)
  if (['register', 'login', 'pilot'].includes(page)) {
    return (
      <BrandProvider>
        <ToastProvider>
          <CartProvider>
            {page === 'register' && (
              <Register 
                onNavigate={navigate} 
                onRegister={handleRegister} 
              />
            )}
            {page === 'login' && (
              <Login 
                onNavigate={navigate} 
                onLogin={handleLogin} 
              />
            )}
            {page === 'pilot' && (
              <PilotEnrollment onNavigate={navigate} />
            )}
          </CartProvider>
        </ToastProvider>
      </BrandProvider>
    );
  }
  
  // Main app with layout
  return (
    <BrandProvider>
      <ToastProvider>
        <CartProvider>
          <AppContent
            page={page}
            detailId={detailId}
            user={user}
            prescriptions={prescriptions}
            orders={orders}
            navigate={navigate}
            handleLogout={handleLogout}
            handleRefresh={handleRefresh}
            setUser={setUser}
            showAdmin={showAdmin}
            brandSwitcherVisible={brandSwitcherVisible}
            setBrandSwitcherVisible={setBrandSwitcherVisible}
          />
        </CartProvider>
      </ToastProvider>
    </BrandProvider>
  );
}

function AppContent({ page, detailId, user, prescriptions, orders, navigate, handleLogout, handleRefresh, setUser, showAdmin, brandSwitcherVisible, setBrandSwitcherVisible }) {
  const { itemCount } = useCart();
  const { brand } = useBrand();

  return (
    <div style={{
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#F9FAFB'
    }}>
      {/* Header */}
      {page !== 'landing' && (
        <Header 
          user={user}
          onNavigate={navigate}
          onLogout={handleLogout}
          currentPage={page}
          cartItemCount={itemCount}
        />
      )}
      
      {/* Brand Switcher */}
      <BrandSwitcher
        visible={brandSwitcherVisible}
        onToggle={() => setBrandSwitcherVisible(v => !v)}
      />
      
      {/* Main Content */}
      <main style={{ flex: 1 }}>
        {/* Landing */}
        {page === 'landing' && (
          <Landing onNavigate={navigate} />
        )}
        
        {/* Patient Pages */}
        {page === 'dashboard' && user && !brand.isB2B && (
          <Dashboard 
            user={user}
            onNavigate={navigate}
            prescriptions={prescriptions}
            orders={orders}
          />
        )}
        
        {page === 'upload' && user && (
          <PrescriptionUpload
            user={user}
            onNavigate={navigate}
            onRefresh={handleRefresh}
          />
        )}
        
        {page === 'prescriptions' && user && (
          <PrescriptionList
            user={user}
            onNavigate={navigate}
          />
        )}
        
        {page === 'rx-detail' && user && (
          <PrescriptionDetail
            prescriptionId={detailId}
            onNavigate={navigate}
          />
        )}
        
        {page === 'bundle' && user && (
          <BundleOrder
            user={user}
            onNavigate={navigate}
            onRefresh={handleRefresh}
          />
        )}
        
        {page === 'orders' && user && (
          <OrderList
            user={user}
            onNavigate={navigate}
          />
        )}
        
        {page === 'order-detail' && user && (
          <OrderDetail
            orderId={detailId}
            onNavigate={navigate}
          />
        )}
        
        {page === 'messages' && user && (
          <Messages
            user={user}
            onNavigate={navigate}
          />
        )}
        
        {page === 'savings' && user && !brand.isB2B && (
          <CostCalculator
            user={user}
            onNavigate={navigate}
          />
        )}
        
        {page === 'profile' && user && (
          <Profile
            user={user}
            onNavigate={navigate}
            onUpdate={setUser}
          />
        )}
        
        {/* B2B Pages */}
        {page === 'dashboard' && user && brand.isB2B && (
          <B2BDashboard
            user={user}
            onNavigate={navigate}
          />
        )}
        
        {page === 'catalog' && user && (
          <Catalog onNavigate={navigate} />
        )}
        
        {page === 'vendors' && user && (
          <VendorConnections
            user={user}
            onNavigate={navigate}
            onSyncComplete={handleRefresh}
          />
        )}
        
        {page === 'purchase-orders' && user && (
          <PurchaseOrders onNavigate={navigate} />
        )}

        {/* Shared Pages - Cart & Checkout */}
        {page === 'cart' && user && (
          <Cart onNavigate={navigate} />
        )}

        {page === 'checkout' && user && (
          <Checkout
            user={user}
            onNavigate={navigate}
            onRefresh={handleRefresh}
          />
        )}
        
        {page === 'savings' && user && brand.isB2B && (
          <SpendAnalysis onNavigate={navigate} />
        )}
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Chatbot (always available when logged in) */}
      {user && (
        <Chatbot
          user={user}
          prescriptions={prescriptions}
          orders={orders}
          onNavigate={navigate}
        />
      )}
      
      {/* Admin Panel (toggle with Shift+A) */}
      {showAdmin && (
        <AdminPanel onRefresh={handleRefresh} />
      )}
    </div>
  );
}

export default App;


/*
 * PRODUCTION MIGRATION NOTES:
 * 
 * 1. ROUTING:
 *    - Replace state-based routing with React Router v6
 *    - Create <Routes> and <Route> components
 *    - Use useNavigate() hook instead of navigate function
 *    - Add protected route wrapper for auth-required pages
 * 
 * 2. AUTHENTICATION:
 *    - Replace DB session check with Auth0/Clerk provider
 *    - Use useAuth() hook from provider
 *    - Implement proper token refresh
 *    - Add loading states during auth check
 * 
 * 3. DATA FETCHING:
 *    - Replace direct DB calls with API fetch/axios
 *    - Add React Query or SWR for caching
 *    - Implement proper error boundaries
 *    - Add loading indicators
 * 
 * 4. SECURITY:
 *    - Move Anthropic API calls to backend
 *    - Add CSRF protection
 *    - Implement rate limiting
 *    - Add input sanitization
 * 
 * 5. PERFORMANCE:
 *    - Lazy load route components
 *    - Add code splitting
 *    - Implement virtual scrolling for long lists
 *    - Optimize re-renders with React.memo
 * 
 * Example React Router structure:
 * 
 * import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
 * 
 * <BrowserRouter>
 *   <Routes>
 *     <Route path="/" element={<Landing />} />
 *     <Route path="/login" element={<Login />} />
 *     <Route path="/register" element={<Register />} />
 *     <Route path="/dashboard" element={
 *       <ProtectedRoute>
 *         <Dashboard />
 *       </ProtectedRoute>
 *     } />
 *     ...etc
 *   </Routes>
 * </BrowserRouter>
 */
